import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY = {
  enrollment_id: "", eligibility: "Pending", resume_status: "Not Started", linkedin_url: "",
  mock_interviews_done: 0, applications_sent: 0, interviews_scheduled: 0, offers_received: 0,
  placed: false, company: "", designation: "", package_lpa: 0, joining_date: "",
  refund_eligibility: "N/A", notes: "",
};

export default function PlacementDialog({ open, onOpenChange, editRow, meta, onSaved }) {
  const [enrollments, setEnrollments] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(editRow ? { ...EMPTY, ...editRow } : EMPTY);
    if (!editRow) api.get("/enrollments", { params: { page_size: 100 } }).then(({ data }) => setEnrollments(data.items || []));
  }, [open, editRow]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const num = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.enrollment_id) { toast.error("Select an enrollment"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        mock_interviews_done: Number(form.mock_interviews_done), applications_sent: Number(form.applications_sent),
        interviews_scheduled: Number(form.interviews_scheduled), offers_received: Number(form.offers_received),
        package_lpa: Number(form.package_lpa),
      };
      if (editRow) await api.put(`/placement/${editRow.id}`, payload);
      else await api.post("/placement", payload);
      toast.success(editRow ? "Placement record updated" : "Placement record created");
      onOpenChange(false); onSaved?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="placement-dialog">
        <DialogHeader>
          <DialogTitle>{editRow ? `Placement — ${editRow.jg_code}` : "New placement record"}</DialogTitle>
          <DialogDescription>Track job-guarantee eligibility, prep and placement outcome.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          {!editRow && (
            <div className="col-span-2">
              <Label>Enrollment</Label>
              <Select value={form.enrollment_id} onValueChange={(v) => setForm((f) => ({ ...f, enrollment_id: v }))}>
                <SelectTrigger data-testid="placement-enrollment-select"><SelectValue placeholder="Select enrollment" /></SelectTrigger>
                <SelectContent>{enrollments.map((e) => <SelectItem key={e.id} value={e.id}>{e.enrollment_code} · {e.student_name} · {e.course_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label>Eligibility</Label>
            <Select value={form.eligibility} onValueChange={(v) => setForm((f) => ({ ...f, eligibility: v }))}>
              <SelectTrigger data-testid="placement-eligibility-select"><SelectValue /></SelectTrigger>
              <SelectContent>{meta.eligibility.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Resume status</Label>
            <Select value={form.resume_status} onValueChange={(v) => setForm((f) => ({ ...f, resume_status: v }))}>
              <SelectTrigger data-testid="placement-resume-select"><SelectValue /></SelectTrigger>
              <SelectContent>{meta.resume_statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Mock interviews</Label><Input type="number" value={form.mock_interviews_done} onChange={num("mock_interviews_done")} /></div>
          <div><Label>Applications sent</Label><Input type="number" value={form.applications_sent} onChange={num("applications_sent")} data-testid="placement-applications-input" /></div>
          <div><Label>Interviews scheduled</Label><Input type="number" value={form.interviews_scheduled} onChange={num("interviews_scheduled")} /></div>
          <div><Label>Offers received</Label><Input type="number" value={form.offers_received} onChange={num("offers_received")} /></div>
          <div className="col-span-2"><Label>LinkedIn URL</Label><Input value={form.linkedin_url} onChange={set("linkedin_url")} /></div>

          <div className="col-span-2 flex items-center gap-2 rounded-lg border border-border bg-slate-50 p-3">
            <Switch checked={form.placed} onCheckedChange={(v) => setForm((f) => ({ ...f, placed: v }))} data-testid="placement-placed-switch" />
            <Label>Placed</Label>
          </div>
          {form.placed && (
            <>
              <div><Label>Company</Label><Input value={form.company} onChange={set("company")} data-testid="placement-company-input" /></div>
              <div><Label>Designation</Label><Input value={form.designation} onChange={set("designation")} /></div>
              <div><Label>Package (LPA)</Label><Input type="number" step="0.1" value={form.package_lpa} onChange={num("package_lpa")} data-testid="placement-package-input" /></div>
              <div><Label>Joining date</Label><Input type="date" value={form.joining_date} onChange={set("joining_date")} /></div>
            </>
          )}
          <div>
            <Label>Refund eligibility</Label>
            <Select value={form.refund_eligibility} onValueChange={(v) => setForm((f) => ({ ...f, refund_eligibility: v }))}>
              <SelectTrigger data-testid="placement-refund-select"><SelectValue /></SelectTrigger>
              <SelectContent>{meta.refund_eligibility.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-2"><Label>Notes</Label><Textarea value={form.notes} onChange={set("notes")} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="save-placement-button">{saving ? "Saving..." : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
